import React from "react";
import { getRenderCount } from "./renderCount.tsx";
import type { EmbeddedPodletResponse } from "./types.ts";

export function usePodletContent(response: EmbeddedPodletResponse) {
  const podletId = React.useId();
  const podletRef = React.useRef<HTMLDivElement | null>(null);
  const hasRerenderedRef = React.useRef(false);

  const renderCount = getRenderCount();

  const isHydrating = renderCount === 1;

  const isHydrationErrorRerendering = renderCount > 1;

  if (isHydrationErrorRerendering) {
    hasRerenderedRef.current = true;
  }

  /**
   * Podlets should be wrapped in Web Components to avoid that styling bleeds in/out of the podlet
   * There’s an issue with isolated podlets (web components) in SSR (Server-Side Rendering) within React.
   * Web components, represented by declarative shadow DOM encapsulated within a template tag, are parsed
   * by the browser and inserted into a shadow DOM.
   * This process results in a mismatch between client-side and server-side rendering. Therefore, on
   * hydration we want to use the content present in the document's innerHTML, as received from the server.
   */
  const innerHTML = React.useMemo(() => {
    return {
      __html: isHydrating ? (document.getElementById(podletId)?.innerHTML ?? response.content) : response.content,
    };
  }, [podletId, response.content, isHydrating]);

  return {
    innerHTML,
    isHydrationErrorRerendering,
    podletId,
    setPodletRef: (element: HTMLDivElement | null) => {
      podletRef.current = element;
    },
  };
}
